import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../../components/SEO';
import RelatedBlogPosts from '../../../components/feature/RelatedBlogPosts';

export default function HamiltonServiceAreaPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const hamiltonStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': 'https://alliancesecuritysystems.com/service-areas/hamilton/#localbusiness',
      name: 'Alliance Security Systems - Hamilton Office',
      description: 'Hamilton\'s trusted security company since 1999. Professional alarm systems, security cameras, access control, and 24/7 live monitoring. 2,100+ local installations across Hamilton, Ancaster, Dundas, Stoney Creek, and Waterdown.',
      telephone: '+1-888-458-9181',
      foundingDate: '1999',
      url: 'https://alliancesecuritysystems.com/service-areas/hamilton',
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '1 Hunter Street East, Ground Floor',
        addressLocality: 'Hamilton',
        addressRegion: 'ON',
        postalCode: 'L8N 3W1',
        addressCountry: 'CA',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '43.2557',
        longitude: '-79.8711',
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '18:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Saturday',
          opens: '09:00',
          closes: '17:00',
          description: 'By Appointment',
        },
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '500',
        bestRating: '5',
      },
      areaServed: [
        { '@type': 'City', name: 'Hamilton' },
        { '@type': 'City', name: 'Ancaster' },
        { '@type': 'City', name: 'Dundas' },
        { '@type': 'City', name: 'Stoney Creek' },
        { '@type': 'City', name: 'Waterdown' },
        { '@type': 'City', name: 'Binbrook' },
        { '@type': 'City', name: 'Flamborough' },
        { '@type': 'City', name: 'Glanbrook' },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Hamilton Security Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Alarm Systems Hamilton', description: 'Professional alarm system installation and 24/7 monitoring for Hamilton homes and businesses.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Security Cameras Hamilton', description: 'HD and 4K security camera systems with night vision and cloud storage for Hamilton properties.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Access Control Hamilton', description: 'Commercial card access and keyless entry systems for Hamilton businesses.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Live Monitoring Hamilton', description: 'Real-time video surveillance monitoring for Hamilton properties.' } },
        ],
      },
      parentOrganization: {
        '@type': 'Organization',
        name: 'Alliance Security Systems',
        url: 'https://alliancesecuritysystems.com',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://alliancesecuritysystems.com' },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: 'https://alliancesecuritysystems.com/service-areas' },
        { '@type': 'ListItem', position: 3, name: 'Hamilton', item: 'https://alliancesecuritysystems.com/service-areas/hamilton' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Security Systems Installation Hamilton',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Alliance Security Systems - Hamilton Office',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '1 Hunter Street East, Ground Floor',
          addressLocality: 'Hamilton',
          addressRegion: 'ON',
          postalCode: 'L8N 3W1',
          addressCountry: 'CA',
        },
      },
      areaServed: {
        '@type': 'City',
        name: 'Hamilton',
        containedInPlace: { '@type': 'AdministrativeArea', name: 'Ontario, Canada' },
      },
      serviceType: ['Alarm System Installation', 'Security Camera Installation', 'Access Control Installation', 'Live Video Monitoring'],
      description: 'Professional security system installation and monitoring services in Hamilton, Ontario. Serving Downtown Hamilton, Westdale, Dundas, Ancaster, Stoney Creek, Waterdown, and surrounding areas since 1999.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Do you serve all Hamilton neighborhoods including Ancaster, Dundas, and Stoney Creek?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes! We serve all Hamilton neighborhoods including Downtown Hamilton, Westdale, Dundas, Ancaster, Stoney Creek, Waterdown, Binbrook, Flamborough, Glanbrook, Mountain, East Hamilton, West Hamilton, North End, Durand, and Kirkendall. Our local office at 1 Hunter Street East ensures fast response times across the entire Hamilton area.'
          }
        },
        {
          '@type': 'Question',
          name: 'How fast is your response time in Hamilton?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Our Hamilton office at 1 Hunter Street East provides response times under 1 hour for most service calls. We prioritize local service and have dedicated technicians stationed in Hamilton to ensure the fastest possible response for installations, repairs, and emergency service calls.'
          }
        },
        {
          '@type': 'Question',
          name: 'Do you install security systems in both residential and industrial areas of Hamilton?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely! Hamilton\'s unique mix of residential neighborhoods and industrial zones requires specialized security expertise. We have extensive experience securing both residential homes in areas like Westdale and Ancaster, as well as industrial facilities in Hamilton\'s manufacturing districts. Our team understands the specific security challenges of both environments.'
          }
        }
      ]
    }
  ];

  const services = [
    {
      icon: 'ri-shield-check-line',
      title: 'Alarm Systems Hamilton',
      description: 'Professional alarm system installation and monitoring for Hamilton homes and businesses. 24/7 protection with instant mobile alerts.',
      link: '/alarm-packages'
    },
    {
      icon: 'ri-camera-line',
      title: 'Security Cameras Hamilton',
      description: 'HD and 4K security camera systems with night vision, motion detection, and cloud storage for Hamilton properties.',
      link: '/camera-packages'
    },
    {
      icon: 'ri-key-2-line',
      title: 'Access Control Hamilton',
      description: 'Commercial card access and keyless entry systems for Hamilton businesses. Secure entry management with detailed logs.',
      link: '/access-control'
    },
    {
      icon: 'ri-live-line',
      title: 'Live Monitoring Hamilton',
      description: 'Real-time video surveillance monitoring for Hamilton properties. Watch your property from anywhere, anytime.',
      link: '/live-monitoring'
    }
  ];

  const neighborhoods = [
    'Downtown Hamilton', 'Westdale', 'Dundas', 'Ancaster', 'Stoney Creek',
    'Waterdown', 'Binbrook', 'Flamborough', 'Glanbrook', 'Mountain',
    'East Hamilton', 'West Hamilton', 'North End', 'Durand', 'Kirkendall'
  ];

  const stats = [
    { number: '2,100+', label: 'Hamilton Installations' },
    { number: '25+', label: 'Years Serving Hamilton' },
    { number: '4.9', label: 'Google Rating' },
    { number: '< 1hr', label: 'Local Response Time' }
  ];

  const localInsights = [
    {
      icon: 'ri-building-line',
      stat: '58%',
      description: 'of Hamilton properties are industrial/commercial, requiring specialized security solutions for both residential and business sectors'
    },
    {
      icon: 'ri-shield-star-line',
      stat: '34%',
      description: 'increase in Hamilton property crime (2022-2023), making professional security systems essential for protection'
    },
    {
      icon: 'ri-home-smile-line',
      stat: '12,000+',
      description: 'new residential units built in Hamilton since 2015, creating high demand for modern security installations'
    }
  ];

  const faqs = [
    {
      question: 'Do you serve all Hamilton neighborhoods including Ancaster, Dundas, and Stoney Creek?',
      answer: 'Yes! We serve all Hamilton neighborhoods including Downtown Hamilton, Westdale, Dundas, Ancaster, Stoney Creek, Waterdown, Binbrook, Flamborough, Glanbrook, Mountain, East Hamilton, West Hamilton, North End, Durand, and Kirkendall. Our local office at 1 Hunter Street East ensures fast response times across the entire Hamilton area.'
    },
    {
      question: 'How fast is your response time in Hamilton?',
      answer: 'Our Hamilton office at 1 Hunter Street East provides response times under 1 hour for most service calls. We prioritize local service and have dedicated technicians stationed in Hamilton to ensure the fastest possible response for installations, repairs, and emergency service calls.'
    },
    {
      question: 'Do you install security systems in both residential and industrial areas of Hamilton?',
      answer: 'Absolutely! Hamilton\'s unique mix of residential neighborhoods and industrial zones requires specialized security expertise. We have extensive experience securing both residential homes in areas like Westdale and Ancaster, as well as industrial facilities in Hamilton\'s manufacturing districts. Our team understands the specific security challenges of both environments.'
    }
  ];

  return (
    <>
      <SEO
        title="Security Systems Hamilton | Alarm & Camera Installation | Alliance Security"
        description="Hamilton's trusted security company since 1999 \u2014 your hub for home security systems, alarm installation, cameras, and expert local guides. 2,100+ installations across Hamilton, Ancaster, Dundas, Stoney Creek, and Waterdown. Free consultation. Call 1-888-458-9181."
        keywords="security systems Hamilton, alarm systems Hamilton, best home security company Hamilton, security cameras Hamilton, access control Hamilton, Hamilton security company, security installation Hamilton ON"
        canonical="/service-areas/hamilton"
        structuredData={hamiltonStructuredData}
      />
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>
                Home
              </Link>
              <Link to="/service-areas" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#D4A853]' : 'text-white'}`}>
                Service Areas
              </Link>
              <a href="tel:18884589181" className="bg-[#D4A853] text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all whitespace-nowrap cursor-pointer">
                1-888-458-9181
              </a>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className={`md:hidden ${isScrolled ? 'bg-white' : 'bg-[#0A1628]/95 backdrop-blur-sm'} border-t ${isScrolled ? 'border-gray-100' : 'border-white/10'} px-4 pb-4 space-y-2`}>
              <Link to="/" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}>
                Home
              </Link>
              <Link to="/service-areas" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-[#D4A853] border-gray-50' : 'text-[#D4A853] border-white/10'}`}>
                Service Areas
              </Link>
              <a href="tel:18884589181" className="block w-full text-center bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold mt-2">
                1-888-458-9181
              </a>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section className="relative min-h-[50vh] sm:min-h-[70vh] flex items-center pt-16 sm:pt-20">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/85210c7fae409e45a6f181f5fce68737.jpg" 
              alt="Hamilton Waterfront"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full py-12 sm:py-20">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <i className="ri-map-pin-2-fill text-[#D4A853] text-lg sm:text-xl"></i>
                <span className="text-[#D4A853] font-semibold text-sm sm:text-base">Hamilton, Ontario</span>
              </div>
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4 sm:mb-6 leading-tight">
                <strong>Security Systems Hamilton</strong> - Protecting The Hammer Since 1999
              </h1>
              <p className="text-base sm:text-xl text-white/90 mb-6 sm:mb-8">
                Hamilton's unique blend of industrial strength and residential charm demands security solutions that protect both. With over 2,100 local installations across residential neighborhoods and commercial districts, we understand The Hammer's specific security needs—from waterfront properties to mountain homes, from manufacturing facilities to downtown businesses.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 sm:gap-4">
                <Link 
                  to="/#contact"
                  className="w-full sm:w-auto bg-[#D4A853] text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg"
                >
                  Get Free Quote in Hamilton
                  <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-arrow-right-line text-lg"></i>
                  </span>
                </Link>
                <a 
                  href="tel:18884589181"
                  className="w-full sm:w-auto border-2 border-white text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer text-center"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </section>

      {/* Breadcrumb */}
      <nav className="bg-white border-b border-gray-100 py-3" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <ol className="flex items-center gap-2 text-xs text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                <span itemProp="name">Home</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/service-areas" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                <span itemProp="name">Service Areas</span>
              </Link>
              <meta itemProp="position" content="2" />
            </li>
            <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-[#D4A853] font-medium" itemProp="name">Hamilton</span>
              <meta itemProp="position" content="3" />
            </li>
          </ol>
        </div>
      </nav>

        {/* Stats Section */}
        <section className="py-8 sm:py-12 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-2xl sm:text-4xl font-bold text-[#D4A853] mb-1 sm:mb-2">{stat.number}</p>
                  <p className="text-white/70 text-xs sm:text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Local Security Insights Section */}
        <section className="py-12 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                HAMILTON SECURITY INSIGHTS
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Why Hamilton Properties Need Professional Security
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Local data and trends specific to Hamilton's security landscape
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {localInsights.map((insight, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6 sm:p-8 border border-gray-100 hover:shadow-lg transition-all">
                  <div className="w-14 h-14 bg-[#D4A853]/10 rounded-full flex items-center justify-center mb-4">
                    <i className={`${insight.icon} text-3xl text-[#D4A853]`}></i>
                  </div>
                  <p className="text-3xl sm:text-4xl font-bold text-[#D4A853] mb-3">{insight.stat}</p>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{insight.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-12 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                HAMILTON SECURITY SERVICES
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Professional Security Solutions in Hamilton
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Comprehensive security services tailored for Hamilton homes and businesses
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8">
              {services.map((service, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#D4A853] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${service.icon} text-2xl sm:text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-3 sm:mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">{service.description}</p>
                  <Link to={service.link} className="text-[#D4A853] font-semibold flex items-center gap-2 hover:gap-3 transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 sm:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                HAMILTON FAQ
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-sm sm:text-lg text-gray-600">
                Common questions about our Hamilton security services
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <span className="font-semibold text-[#0A1628] pr-4 text-sm sm:text-base">{faq.question}</span>
                    <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-2xl text-[#D4A853] flex-shrink-0`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Hamilton Security Resource Centre */}
        <section className="py-16 sm:py-24 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/20 text-[#D4A853] px-5 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 uppercase tracking-widest whitespace-nowrap">
                Hamilton Security Resource Centre
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4">
                The Definitive Hamilton Security Reading List
              </h2>
              <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto">
                Two authoritative, independently-researched guides built specifically for Hamilton's security landscape — covering every angle from alarm systems to the full home security company comparison.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-10 sm:mb-14">
              {/* Guide 1: Best Alarm System Company */}
              <a
                href="/blog/best-alarm-system-company-hamilton"
                className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 hover:border-[#D4A853]/40 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-52 sm:h-60 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Hamilton%20Ontario%20residential%20neighbourhood%20at%20golden%20hour%20mature%20tree-lined%20street%20detached%20brick%20home%20with%20professional%20security%20system%20panel%20glowing%20near%20front%20entrance%20well-kept%20lawn%20escarpment%20backdrop%20warm%20amber%20evening%20light%20established%20community&width=800&height=500&seq=blog20&orientation=landscape"
                    alt="Best Alarm System Company Hamilton"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#D4A853] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide whitespace-nowrap">
                      Alarm Systems Guide
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white/80 text-xs">
                    <i className="ri-time-line"></i>
                    <span>14 min read</span>
                    <span className="mx-1">·</span>
                    <i className="ri-calendar-line"></i>
                    <span>July 2025</span>
                  </div>
                </div>
                <div className="p-6 sm:p-8">
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-3 group-hover:text-[#D4A853] transition-colors leading-snug">
                    Best Alarm System Company in Hamilton: The Complete 2025 Guide
                  </h3>
                  <p className="text-white/65 text-sm leading-relaxed mb-5">
                    Covers ULC-certified monitoring, Hamilton Police Service response protocols, neighbourhood-by-neighbourhood risk profiles from Beasley to Ancaster, false alarm bylaw costs, and the exact questions to ask any alarm company before signing a contract.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {['ULC Monitoring', 'HPS Response', 'Neighbourhood Risk', 'Contract Terms'].map((tag) => (
                      <span key={tag} className="bg-white/10 text-white/70 text-xs px-3 py-1 rounded-full whitespace-nowrap">{tag}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-[#D4A853] font-semibold text-sm group-hover:gap-3 transition-all">
                    Read Full Guide <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </a>

              {/* Guide 2: Best Home Security Company */}
              <a
                href="/blog/best-home-security-company-hamilton"
                className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 hover:border-[#D4A853]/40 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-52 sm:h-60 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Hamilton%20Ontario%20residential%20neighbourhood%20at%20dusk%20mature%20tree-lined%20street%20established%20brick%20detached%20home%20with%20professional%20security%20camera%20mounted%20above%20front%20entrance%20warm%20porch%20lighting%20well-maintained%20lawn%20escarpment%20backdrop%20golden%20amber%20evening%20light%20safe%20community%20Golden%20Horseshoe%20Canada%20clear%20sky&width=800&height=500&seq=blog28&orientation=landscape"
                    alt="Best Home Security Company Hamilton"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#D4A853] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide whitespace-nowrap">
                      Company Ranking Guide
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white/80 text-xs">
                    <i className="ri-time-line"></i>
                    <span>18 min read</span>
                    <span className="mx-1">·</span>
                    <i className="ri-calendar-line"></i>
                    <span>March 2026</span>
                  </div>
                </div>
                <div className="p-6 sm:p-8">
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-3 group-hover:text-[#D4A853] transition-colors leading-snug">
                    Best Home Security Company in Hamilton: The Definitive 2026 Ranking
                  </h3>
                  <p className="text-white/65 text-sm leading-relaxed mb-5">
                    Ranks Hamilton's security providers head-to-head across urban lower-city and suburban Mountain risk profiles, gentrification security gaps, rear laneway camera coverage, QEW-403 vehicle theft corridor data, and the 5-year contract math — with equipment ownership spelled out in plain arithmetic.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {['Company Rankings', 'Urban vs Suburban', 'Vehicle Theft Data', 'Pricing Breakdown'].map((tag) => (
                      <span key={tag} className="bg-white/10 text-white/70 text-xs px-3 py-1 rounded-full whitespace-nowrap">{tag}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-[#D4A853] font-semibold text-sm group-hover:gap-3 transition-all">
                    Read Full Guide <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </a>
            </div>

            {/* Guide 3: Commercial Security */}
            <div className="mb-10 sm:mb-14">
              <a
                href="/blog/commercial-security-company-hamilton"
                className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 hover:border-[#D4A853]/40 transition-all duration-300 cursor-pointer flex flex-col lg:flex-row"
              >
                <div className="relative lg:w-80 flex-shrink-0 h-52 lg:h-auto overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Hamilton%20Ontario%20industrial%20waterfront%20commercial%20district%20at%20dusk%20steel%20manufacturing%20facilities%20and%20modern%20business%20buildings%20with%20professional%20security%20cameras%20mounted%20on%20exterior%20walls%20amber%20industrial%20lighting%20escarpment%20backdrop%20Golden%20Horseshoe%20Canada%20clear%20evening%20sky&width=800&height=500&seq=blog29&orientation=landscape"
                    alt="Best Commercial Security Company Hamilton"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#D4A853] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide whitespace-nowrap">
                      Commercial Security Guide
                    </span>
                  </div>
                </div>
                <div className="p-6 sm:p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-3">
                    <i className="ri-building-2-line text-[#D4A853] text-xl"></i>
                    <span className="text-[#D4A853] text-xs font-semibold uppercase tracking-widest">NEW — 2026 Industrial & Business Edition</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-3 group-hover:text-[#D4A853] transition-colors leading-snug">
                    Best Commercial Security Company in Hamilton: The Definitive 2026 Industrial Guide
                  </h3>
                  <p className="text-white/65 text-sm leading-relaxed mb-5">
                    Covers Hamilton's industrial waterfront corridor, AEGD logistics zone, QEW-403 cargo theft patterns (up 51% since 2021), multi-door dock access control for manufacturing facilities, LPR camera integration for Hamilton Police Service Commercial Crime Unit investigations, and the standalone municipal force distinction that GTA-template providers miss entirely.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {['Industrial Corridor', 'QEW-403 Cargo Theft', 'AEGD Zone', 'Dock Access Control', 'LPR Cameras'].map((tag) => (
                      <span key={tag} className="bg-white/10 text-white/70 text-xs px-3 py-1 rounded-full whitespace-nowrap">{tag}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-[#D4A853] font-semibold text-sm group-hover:gap-3 transition-all">
                    Read Full Guide <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </a>
            </div>

            {/* Hub value proposition */}
            <div className="border-t border-white/10 pt-8 sm:pt-10">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                {[
                  { icon: 'ri-search-eye-line', label: 'Independent Research', desc: 'No paid placements — rankings based entirely on monitoring certifications, contract transparency, and local track record' },
                  { icon: 'ri-map-pin-2-line', label: 'Hamilton-Specific', desc: 'Every neighbourhood, every risk profile, every bylaw detail is specific to Hamilton \u2014 not repurposed GTA content' },
                  { icon: 'ri-refresh-line', label: 'Updated 2026', desc: 'Pricing, crime statistics, and company details verified against the most current available Hamilton data' },
                ].map((item, i) => (
                  <div key={i} className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 bg-[#D4A853]/15 rounded-full flex items-center justify-center">
                      <i className={`${item.icon} text-2xl text-[#D4A853]`}></i>
                    </div>
                    <p className="font-semibold text-white text-sm">{item.label}</p>
                    <p className="text-white/55 text-xs leading-relaxed max-w-xs">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

      {/* Related Blog Posts */}
      <RelatedBlogPosts city="hamilton" />

        {/* Why a Local Expert Matters */}
        <section className="py-12 sm:py-24 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/20 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">LOCAL ADVANTAGE</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">Why a Local Security Expert Matters in Hamilton</h2>
              <p className="text-sm sm:text-lg text-white/70 max-w-2xl mx-auto">National chains can't match the speed, knowledge, and accountability of a true local provider</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {[
                {
                  icon: 'ri-timer-flash-line',
                  title: 'Under 1-Hour Response',
                  description: 'Our Hamilton office at 1 Hunter Street East means technicians are always nearby — not dispatched from a distant call centre. Hamilton emergencies get Hamilton-speed responses.',
                },
                {
                  icon: 'ri-government-line',
                  title: 'Hamilton Bylaw Knowledge',
                  description: 'We know Hamilton\'s alarm permit requirements, false alarm bylaws, and City of Hamilton municipal regulations — so your system is always fully compliant.',
                },
                {
                  icon: 'ri-user-star-line',
                  title: 'Local References',
                  description: '"Alliance secured our Ancaster home and our industrial facility in the east end — they understood Hamilton\'s unique mix of residential and commercial needs perfectly." — Hamilton client',
                },
                {
                  icon: 'ri-percent-line',
                  title: 'Ontario Insurance Discounts',
                  description: 'Monitored security systems qualify for up to 20% home insurance discounts in Ontario. Our ULC-certified monitoring satisfies all major insurers including Intact and Aviva.',
                },
              ].map((point, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 sm:p-8 hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 bg-[#D4A853]/20 rounded-full flex items-center justify-center mb-4">
                    <i className={`${point.icon} text-2xl text-[#D4A853]`}></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3">{point.title}</h3>
                  <p className="text-white/65 text-xs sm:text-sm leading-relaxed">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-12 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                  WHY HAMILTON TRUSTS US
                </span>
                <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-4 sm:mb-6">
                  Hamilton's Most Trusted Security Company
                </h2>
                <p className="text-gray-600 mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base">
                  For over 25 years, we've been protecting Hamilton homes and businesses with state-of-the-art security solutions.
                </p>
                
                <div className="space-y-3 sm:space-y-4">
                  {[
                    'Licensed and insured Hamilton security professionals',
                    'Same-day service available across Hamilton',
                    'Factory-certified technicians for all major brands',
                    '24/7 local monitoring and support',
                    'Free on-site security assessments'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="relative w-full h-[300px] sm:h-[500px] rounded-2xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20security%20technician%20installing%20security%20camera%20system%20in%20modern%20Hamilton%20home%20interior%20clean%20professional%20work%20environment&width=800&height=600&seq=ham2&orientation=landscape" 
                  alt="Security Installation Hamilton"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Neighborhoods Section */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                SERVICE COVERAGE
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Hamilton Neighborhoods We Serve
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Providing security services across all Hamilton neighborhoods
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {neighborhoods.map((neighborhood, index) => (
                <div 
                  key={index}
                  className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center hover:bg-[#D4A853]/10 hover:border-[#D4A853]/30 border border-gray-100 transition-all duration-300"
                >
                  <span className="text-[#0A1628] font-medium text-xs sm:text-sm">{neighborhood}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Office Location & Map Section */}
        <section className="py-12 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                OUR LOCATION
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Visit Our Hamilton Office
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-2-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-2">Office Address</h3>
                    <p className="text-gray-700 leading-relaxed">
                      1 Hunter Street East, Ground Floor<br />
                      Hamilton, ON L8N 3W1
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-2">Phone</h3>
                    <a href="tel:18884589181" className="text-[#D4A853] font-semibold text-lg hover:underline cursor-pointer">
                      1-888-458-9181
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 mb-8">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-2">Business Hours</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Monday - Friday: 8:00 AM - 6:00 PM<br />
                      Saturday: 9:00 AM - 5:00 PM (By Appointment)<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>

                <Link
                  to="/#contact"
                  className="w-full bg-[#D4A853] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap text-center flex items-center justify-center gap-2"
                >
                  Schedule Consultation
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="w-full h-[400px] sm:h-[500px] rounded-2xl overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d92682.45678901234!2d-79.87!3d43.2557!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882c9b8e0daec74b%3A0x67e2c6f8565a8b8e!2sHamilton%2C%20ON!5e0!3m2!1sen!2sca!4v1234567891"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Hamilton Service Area Map"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-24 bg-[#0A1628]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4 sm:mb-6">
              Ready to Secure Your Hamilton Property?
            </h2>
            <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8">
              Get a free security assessment from Hamilton's most trusted security experts
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4">
              <Link 
                to="/#contact"
                className="w-full sm:w-auto bg-[#D4A853] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap text-center"
              >
                Schedule Free Consultation
              </Link>
              <a 
                href="tel:18884589181"
                className="w-full sm:w-auto border-2 border-white text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap text-center"
              >
                1-888-458-9181
              </a>
            </div>
          </div>
        </section>

        {/* Commercial Security Cross-Link Banner */}
        <section className="py-12 bg-gradient-to-r from-[#D4A853] to-[#B8923D]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <i className="ri-building-2-line text-white text-2xl"></i>
                  <h3 className="text-xl sm:text-2xl font-bold text-white">Looking for Commercial Security in Hamilton?</h3>
                </div>
                <p className="text-white/90 text-sm sm:text-base">
                  Explore our dedicated commercial security solutions for Hamilton businesses — cameras, alarms, access control &amp; monitoring for industrial, retail, and office properties.
                </p>
              </div>
              <Link
                to="/commercial-security/hamilton"
                className="bg-white text-[#D4A853] px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-sm sm:text-base font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                View Commercial Security Hamilton <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Other Service Areas */}
        <section className="py-10 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h3 className="text-xl sm:text-2xl font-bold text-[#1E293B] mb-6 sm:mb-8 text-center">Other GTA Service Areas</h3>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {[
                { name: 'Toronto', slug: 'toronto' },
                { name: 'Mississauga', slug: 'mississauga' },
                { name: 'Brampton', slug: 'brampton' },
                { name: 'Oakville', slug: 'oakville' },
                { name: 'Richmond Hill', slug: 'richmond-hill' },
                { name: 'Vaughan', slug: 'vaughan' },
                { name: 'Whitby', slug: 'whitby' },
                { name: 'Ajax', slug: 'ajax' },
                { name: 'Oshawa', slug: 'oshawa' },
              ].map((city) => (
                <Link
                  key={city.slug}
                  to={`/service-areas/${city.slug}`}
                  className="bg-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-medium text-gray-700 hover:text-[#D4A853] hover:shadow-md transition-all cursor-pointer border border-gray-200"
                >
                  {city.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#050B14] text-white py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
              <div className="flex items-center gap-3">
                <div className="flex flex-col leading-none">
                  <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                  <span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
                </div>
                <span className="text-white/60 text-xs sm:text-sm">Hamilton Security Experts Since 1999</span>
              </div>
              <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
                <Link to="/" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Home</Link>
                <Link to="/service-areas" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Service Areas</Link>
                <Link to="/privacy-policy" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Privacy Policy</Link>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
